import React, { useState, useEffect } from 'react';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import ConfirmDialog, { DeleteIcon } from '@/components/common/ConfirmDialog';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import { FaCar, FaCalendarAlt, FaUserAlt, FaPen, FaTrash, FaRoad, FaHistory } from 'react-icons/fa';
import { normalizeDataObject } from '../../utils/normalizationUtils';
import moment from 'moment';

/**
 * Componente para mostrar un vehículo individual
 * Versión móvil exactamente como la imagen 2
 * Versión escritorio con botones pequeños horizontales a la derecha
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.vehiculo - Datos del vehículo
 * @param {Array} props.categorias - Lista de categorías/tipos de vehículo disponibles
 * @param {function} props.onEdit - Función para editar el vehículo
 * @param {function} props.onDelete - Función para eliminar el vehículo
 * @param {function} props.onViewHistory - Función para ver el historial de mantenimientos
 * @returns {JSX.Element} - Componente de ítem de vehículo
 */
const VehiculoItem = ({ vehiculo, categorias = [], onEdit, onDelete, onViewHistory }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Normalizar datos del vehículo para manejar cualquier formato
  const normalizedVehiculo = normalizeDataObject(vehiculo, 'vehiculo');
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);
    
    // Limpiar al desmontar
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Función para obtener el tipo de vehículo por ID
  const getTipoVehiculo = () => {
    const categoriaId = normalizedVehiculo.categoriaId || normalizedVehiculo.categoria_id;
    if (!categoriaId) return null;
    const categoria = categorias.find(cat => cat.id === categoriaId);
    return categoria ? categoria.nombre : null;
  };
  
  // Función para obtener el color de borde según el año del vehículo
  const getBorderColor = () => {
    const currentYear = new Date().getFullYear();
    const vehicleYear = normalizedVehiculo.año || normalizedVehiculo.Año;
    const age = currentYear - vehicleYear;
    
    if (age <= 5) return 'border-green-500'; // Nuevo
    if (age <= 10) return 'border-blue-500'; // Semi-nuevo
    if (age <= 20) return 'border-yellow-500'; // Usado
    return 'border-red-500'; // Antiguo
  };
  
  // Determinar estado del vehículo
  const getEstadoVehiculo = () => {
    const currentYear = new Date().getFullYear();
    const vehicleYear = normalizedVehiculo.año || normalizedVehiculo.Año;
    const age = currentYear - vehicleYear;
    
    if (age <= 5) return { label: 'Nuevo', variant: 'success' };
    if (age <= 10) return { label: 'Semi-nuevo', variant: 'info' };
    if (age <= 20) return { label: 'Usado', variant: 'warning' };
    return { label: 'Antiguo', variant: 'danger' };
  };
  
  const estadoVehiculo = getEstadoVehiculo();
  
  // Función para manejar el clic en el card (abrir historial)
  const handleCardClick = (e) => {
    // Evitar que se abra el modal si se hace clic en los botones de acción
    if (e.target.closest('button') || e.target.closest('[data-action-button]')) {
      console.log('🛑 Clic en botón, no abrir historial');
      return;
    }
    console.log('👆 Clic en card, abriendo historial para vehículo:', normalizedVehiculo.patente);
    e.preventDefault();
    e.stopPropagation();
    
    // Abrir el historial de mantenimientos
    if (onViewHistory) {
      onViewHistory(normalizedVehiculo.patente || normalizedVehiculo.Patente);
    }
  };
  
  return (
    <>
      <div 
        onClick={handleCardClick}
        className={`card mb-3 border-l-4 ${getBorderColor()} transition-all duration-300 cursor-pointer hover:bg-gray-700 shadow-md`}
      >
        <div className="p-0">
          {isMobile ? (
            // Versión móvil - Exactamente como imagen 2
            <div>
              <div className="p-4">
                {/* Patente y estado */}
                <div className="flex items-center mb-3">
                  <PatenteDisplay patente={normalizedVehiculo.patente || normalizedVehiculo.Patente} size="sm" className="mr-2" />
                  
                  <Badge variant={estadoVehiculo.variant} size="sm">
                    {estadoVehiculo.label}
                  </Badge>
                  
                  {getTipoVehiculo() && (
                    <Badge variant="secondary" size="sm" className="ml-2">
                      {getTipoVehiculo()}
                    </Badge>
                  )}
                </div>
                
                {/* Información del vehículo */}
                <div className="text-sm">
                  <div className="flex items-center mb-2">
                    <FaCar className="mr-2 text-gray-500" />
                    <span className="font-semibold">
                      {normalizedVehiculo.marcaLocal || normalizedVehiculo.marca_local || normalizedVehiculo.Marca} {normalizedVehiculo.modeloLocal || normalizedVehiculo.modelo_local || normalizedVehiculo.Modelo} ({normalizedVehiculo.año || normalizedVehiculo.Año})
                    </span>
                  </div>
                  
                  <div className="flex items-center mb-2">
                    <FaCalendarAlt className="mr-2 text-gray-500" />
                    <span className="text-gray-300">Último mantenimiento: {normalizedVehiculo.ultimo_mantenimiento_fecha ? moment(normalizedVehiculo.ultimo_mantenimiento_fecha).format('DD/MM/YYYY') : 'Sin registro'}</span>
                  </div>
                  
                  {(normalizedVehiculo.kilometraje || normalizedVehiculo.Kilometraje) && (
                    <div className="flex items-center mb-2">
                      <FaRoad className="mr-2 text-gray-500" />
                      <span className="text-gray-300">
                        Km: {(normalizedVehiculo.kilometraje || normalizedVehiculo.Kilometraje)?.toLocaleString()}
                      </span>
                    </div>
                  )}
                  
                  {(normalizedVehiculo.clienteNombre || normalizedVehiculo.cliente_nombre || normalizedVehiculo.ClienteNombre) && (
                    <div className="flex items-center text-gray-400 text-sm">
                      <FaUserAlt className="mr-2 text-gray-600" />
                      <span>Propietario: {normalizedVehiculo.clienteNombre || normalizedVehiculo.cliente_nombre || normalizedVehiculo.ClienteNombre}</span>
                      {(normalizedVehiculo.clienteTelefono || normalizedVehiculo.ClienteTelefono) && (
                        <span className="ml-1">({normalizedVehiculo.clienteTelefono || normalizedVehiculo.ClienteTelefono})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botones móviles como en la imagen 2 */}
              <div className="flex justify-between border-t border-gray-700 bg-gray-800" data-action-button>
                <button
                  onClick={() => onEdit(vehiculo)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                  title="Editar"
                  data-action-button
                >
                  <FaPen className="text-lg" />
                  <span className="text-xs mt-1">Editar</span>
                </button>
                
                <button
                  onClick={() => onViewHistory && onViewHistory(normalizedVehiculo.patente || normalizedVehiculo.Patente)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-blue-500"
                  title="Historial"
                  data-action-button
                >
                  <FaHistory className="text-lg" />
                  <span className="text-xs mt-1">Historial</span>
                </button>
                
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                  title="Eliminar"
                  data-action-button
                >
                  <FaTrash className="text-lg" />
                  <span className="text-xs mt-1">Eliminar</span>
                </button>
              </div>
            </div>
          ) : (
            // Versión escritorio - Aprovechando todo el ancho con botones pequeños horizontales
            <div className="flex justify-between items-center p-4 min-h-[80px]">
              {/* Toda la información del vehículo */}
              <div className="flex-grow grid grid-cols-1 md:grid-cols-3 gap-x-4 items-center">
                <div className="col-span-1">
                  <div className="flex items-center">
                    <PatenteDisplay patente={normalizedVehiculo.patente || normalizedVehiculo.Patente} size="sm" className="mr-2" />
                    
                    <Badge variant={estadoVehiculo.variant} size="sm">
                      {estadoVehiculo.label}
                    </Badge>
                    
                    {getTipoVehiculo() && (
                      <Badge variant="secondary" size="sm" className="ml-2">
                        {getTipoVehiculo()}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="col-span-2 text-sm">
                  {/* Primera fila: Marca/Modelo y Año */}
                  <div className="grid grid-cols-2 gap-x-2 mb-1">
                    <div className="flex items-center">
                      <FaCar className="mr-2 text-gray-500" />
                      <span className="font-semibold">
                        {normalizedVehiculo.marcaLocal || normalizedVehiculo.marca_local || normalizedVehiculo.Marca} {normalizedVehiculo.modeloLocal || normalizedVehiculo.modelo_local || normalizedVehiculo.Modelo} ({normalizedVehiculo.año || normalizedVehiculo.Año})
                      </span>
                    </div>
                    
                    <div className="flex items-center">
                      <FaCalendarAlt className="mr-2 text-gray-500" />
                      <span className="text-gray-300">Último mantenimiento: {normalizedVehiculo.ultimo_mantenimiento_fecha ? moment(normalizedVehiculo.ultimo_mantenimiento_fecha).format('DD/MM/YYYY') : 'Sin registro'}</span>
                    </div>
                  </div>
                  
                  {/* Segunda fila: Propietario y Kilometraje */}
                  <div className="grid grid-cols-2 gap-x-2">
                    <div className="flex items-center text-gray-400 text-sm">
                      {(normalizedVehiculo.clienteNombre || normalizedVehiculo.cliente_nombre || normalizedVehiculo.ClienteNombre) && (
                        <>
                          <FaUserAlt className="mr-2 text-gray-600" />
                          <span>{normalizedVehiculo.clienteNombre || normalizedVehiculo.cliente_nombre || normalizedVehiculo.ClienteNombre}</span>
                          {(normalizedVehiculo.clienteTelefono || normalizedVehiculo.ClienteTelefono) && (
                            <span className="ml-1">({normalizedVehiculo.clienteTelefono || normalizedVehiculo.ClienteTelefono})</span>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div className="flex items-center text-gray-300 text-sm">
                      {(normalizedVehiculo.kilometraje || normalizedVehiculo.Kilometraje) && (
                        <>
                          <FaRoad className="mr-2 text-gray-500" />
                          <span>{(normalizedVehiculo.kilometraje || normalizedVehiculo.Kilometraje)?.toLocaleString()} km</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Botones pequeños en línea horizontal */}
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => onEdit(vehiculo)}
                  className="text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded flex items-center justify-center min-w-[2.4rem] h-7"
                  title="Editar"
                  data-action-button
                >
                  <FaPen />
                </button>
                
                <button
                  onClick={() => onViewHistory && onViewHistory(normalizedVehiculo.patente || normalizedVehiculo.Patente)}
                  className="text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded flex items-center justify-center min-w-[2.4rem] h-7"
                  title="Historial"
                  data-action-button  
                >
                  <FaHistory />
                </button>
                
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded flex items-center justify-center min-w-[2.4rem] h-7"
                  title="Eliminar"
                  data-action-button
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          onDelete(normalizedVehiculo.patente || normalizedVehiculo.Patente);
          setIsDeleteDialogOpen(false);
        }}
        title="Eliminar vehículo"
        message={`¿Estás seguro de que deseas eliminar el vehículo ${normalizedVehiculo.marcaLocal || normalizedVehiculo.marca_local || normalizedVehiculo.Marca} ${normalizedVehiculo.modeloLocal || normalizedVehiculo.modelo_local || normalizedVehiculo.Modelo} (${normalizedVehiculo.patente || normalizedVehiculo.Patente})? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmVariant="danger"
        icon={<DeleteIcon />}
      />
    </>
  );
};

export default VehiculoItem;